import { Input, Textarea } from '../forms'
import {
  BERICHT_LEER, ERGEBNISSE, ERGEBNIS_PERSONEN, SCHLEIFEN, ergebnisText, schleifenText,
  type EinsatzBerichtWerte,
} from './bericht'

/** Umschaltknopf (mind. 48 px hoch, tablet-tauglich); ein zweites Tippen hebt die Auswahl auf, wenn `aufheben` gesetzt ist */
function Knopf({ aktiv, onClick, disabled, children, testid }: { aktiv: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode; testid?: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={aktiv} data-testid={testid}
      className={`min-h-[48px] rounded-lg border px-4 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
        aktiv ? 'border-primary-400 bg-primary-600/30 text-dark-text' : 'border-dark-border bg-dark-bg-tertiary/50 text-dark-text-muted hover:bg-dark-bg-tertiary'}`}>
      {children}
    </button>
  )
}

/**
 * Felder für den Einsatzbericht: Einsatznummer, alarmierende Stelle, Stichwort (Freitext, freiwillig), alarmierte Schleifen
 * (Mehrfachauswahl) und Ergebnis (Knöpfe + Zusatztext). Bei „Gefunden“ erscheint die zweite Reihe lebend/verstorben/keine Person.
 * Tippen auf einen gewählten Knopf hebt die Auswahl auf. Alles ist freiwillig und sperrt nichts.
 */
export function EinsatzBerichtFelder({ werte, onChange, disabled }: {
  werte: EinsatzBerichtWerte
  onChange: (neu: EinsatzBerichtWerte) => void
  disabled?: boolean
}) {
  const w = { ...BERICHT_LEER, ...werte }
  const set = (patch: Partial<EinsatzBerichtWerte>) => onChange({ ...w, ...patch })
  const schleife = (k: string) => set({ alarmierte_schleifen: w.alarmierte_schleifen.includes(k) ? w.alarmierte_schleifen.filter(x => x !== k) : [...w.alarmierte_schleifen, k] })
  const ergebnis = (k: string) => set(w.ergebnis === k ? { ergebnis: '', ergebnis_person: '' } : { ergebnis: k, ergebnis_person: k === 'GEFUNDEN' ? w.ergebnis_person : '' })
  return (
    <div className="space-y-4 border-t border-dark-border pt-4" data-testid="bericht-felder">
      <h3 className="text-sm font-semibold text-dark-text">Angaben für den Einsatzbericht</h3>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="min-w-0">
          <label className="label" htmlFor="eb-nummer">Einsatznummer</label>
          <Input id="eb-nummer" value={w.einsatznummer} maxLength={100} onChange={e => set({ einsatznummer: e.target.value })} disabled={disabled} placeholder="z. B. aus der Leitstelle" />
        </div>
        <div className="min-w-0">
          <label className="label" htmlFor="eb-stelle">Alarmierende Stelle</label>
          <Input id="eb-stelle" value={w.alarmierende_stelle} maxLength={200} onChange={e => set({ alarmierende_stelle: e.target.value })} disabled={disabled} placeholder="z. B. Leitstelle, Polizei" />
        </div>
        <div className="min-w-0">
          <label className="label" htmlFor="eb-stichwort">Einsatzstichwort</label>
          <Input id="eb-stichwort" value={w.einsatzstichwort} maxLength={200} onChange={e => set({ einsatzstichwort: e.target.value })} disabled={disabled} placeholder="z. B. Person vermisst" />
        </div>
      </div>
      <div>
        <span className="label">Alarmierte Schleife</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Alarmierte Schleife">
          {SCHLEIFEN.map(s => <Knopf key={s.key} aktiv={w.alarmierte_schleifen.includes(s.key)} onClick={() => schleife(s.key)} disabled={disabled} testid={`schleife-${s.key}`}>{s.label}</Knopf>)}
        </div>
      </div>
      <div>
        <span className="label">Ergebnis</span>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Ergebnis">
          {ERGEBNISSE.map(x => <Knopf key={x.key} aktiv={w.ergebnis === x.key} onClick={() => ergebnis(x.key)} disabled={disabled} testid={`ergebnis-${x.key}`}>{x.label}</Knopf>)}
        </div>
        {w.ergebnis === 'GEFUNDEN' && (
          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Zustand der Person" data-testid="ergebnis-person-reihe">
            {ERGEBNIS_PERSONEN.map(x => <Knopf key={x.key} aktiv={w.ergebnis_person === x.key} onClick={() => set({ ergebnis_person: w.ergebnis_person === x.key ? '' : x.key })} disabled={disabled} testid={`person-${x.key}`}>{x.label}</Knopf>)}
          </div>
        )}
        <div className="mt-3">
          <label className="label" htmlFor="eb-ergebnis-text">Zusatz zum Ergebnis</label>
          <Textarea id="eb-ergebnis-text" rows={2} value={w.ergebnis_text} onChange={e => set({ ergebnis_text: e.target.value })} disabled={disabled} placeholder="Freitext (freiwillig)" />
        </div>
      </div>
    </div>
  )
}

/** Ruhige Anzeige (Detailansicht): fehlende Werte als „—“ */
export function EinsatzBerichtAnzeige({ einsatz }: { einsatz: Record<string, any> }) {
  const zeile = (k: string, v: React.ReactNode, id: string) => (
    <div className="flex justify-between gap-4" data-testid={id}><span className="shrink-0 text-dark-text-muted">{k}:</span> <span className="text-right [overflow-wrap:anywhere]">{v || '—'}</span></div>
  )
  return (
    <>
      {zeile('Einsatznummer', einsatz.einsatznummer, 'anz-nummer')}
      {zeile('Alarmierende Stelle', einsatz.alarmierende_stelle, 'anz-stelle')}
      {zeile('Einsatzstichwort', einsatz.einsatzstichwort, 'anz-stichwort')}
      {zeile('Alarmierte Schleife', schleifenText(einsatz.alarmierte_schleifen), 'anz-schleifen')}
      {zeile('Ergebnis', ergebnisText(einsatz), 'anz-ergebnis')}
      {zeile('Zusatz zum Ergebnis', einsatz.ergebnis_text, 'anz-ergebnis-text')}
    </>
  )
}
